/**
 * Meta Pixel browser tracking.
 *
 * Pixel-only mode (current): fire events to `fbq('track', ...)` exclusively.
 * The CAPI proxy POST is gated behind `VITE_META_CAPI_ENABLED=true` — when
 * the team provisions the server-side access token and flips that flag, the
 * helper automatically pairs every browser hit with a server-side hit using
 * the same `event_id` so Meta dedupes them. Until then, this is pure Pixel.
 *
 * Usage:
 *   trackDualEvent("Purchase", { value: 29.99, currency: "USD" });
 *
 * All errors are swallowed — analytics must never break the UI.
 */
const CAPI_ENABLED = import.meta.env.VITE_META_CAPI_ENABLED === "true";

declare global {
  interface Window {
    fbq?: (
      command: string,
      eventName: string,
      params?: Record<string, unknown>,
      options?: { eventID?: string },
    ) => void;
  }
}

const ENDPOINT = "/api/meta-capi/event";

export type DualEventName =
  | "PageView"
  | "ViewContent"
  | "Search"
  | "AddToCart"
  | "InitiateCheckout"
  | "AddPaymentInfo"
  | "Purchase"
  | "Lead"
  | "CompleteRegistration"
  | "Subscribe"
  | "Contact";

export interface DualEventUserData {
  email?: string | null;
  phone?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  city?: string | null;
  state?: string | null;
  zip_code?: string | null;
  country?: string | null;
  external_id?: string | null;
}

/** Generate a UUID for event_id. Falls back to crypto-strong random if no UUID API. */
function genEventId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    try {
      return crypto.randomUUID();
    } catch {
      // fall through
    }
  }
  return `evt-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 11)}`;
}

function readCookie(name: string): string | undefined {
  if (typeof document === "undefined") return undefined;
  const match = document.cookie.match(
    new RegExp(`(?:^|; )${name.replace(/[$()*+./?[\\\]^{|}-]/g, "\\$&")}=([^;]*)`),
  );
  return match ? decodeURIComponent(match[1]) : undefined;
}

function readFbclidFromUrl(): string | undefined {
  if (typeof window === "undefined") return undefined;
  try {
    const params = new URLSearchParams(window.location.search);
    return params.get("fbclid") ?? undefined;
  } catch {
    return undefined;
  }
}

/**
 * Fire an event to both the browser Pixel AND the server CAPI proxy with a
 * shared event_id. Returns the event_id so callers can correlate.
 *
 * Both calls are best-effort:
 *   - if fbq isn't loaded yet, the browser hit is skipped (queue is missed)
 *   - if the network fails, the server hit is silently dropped
 * Neither failure throws.
 */
export function trackDualEvent(
  name: DualEventName | string,
  customData?: Record<string, unknown>,
  userData?: DualEventUserData,
): string {
  const eventId = genEventId();
  const eventTime = Math.floor(Date.now() / 1000);

  // Browser Pixel hit — uses fbq options.eventID for dedup.
  try {
    if (typeof window !== "undefined" && typeof window.fbq === "function") {
      window.fbq("track", name, customData ?? {}, { eventID: eventId });
    }
  } catch {
    // never break the page
  }

  // Server CAPI hit — gated. Disabled until VITE_META_CAPI_ENABLED=true.
  if (
    CAPI_ENABLED &&
    typeof fetch !== "undefined" &&
    typeof window !== "undefined"
  ) {
    const payload = {
      event_name: name,
      event_id: eventId,
      event_time: eventTime,
      event_source_url: window.location.href,
      action_source: "website" as const,
      user_data: {
        ...userData,
        fbp: readCookie("_fbp") ?? null,
        fbc: readCookie("_fbc") ?? null,
        fbclid: readFbclidFromUrl() ?? null,
      },
      custom_data: customData,
    };

    // Prefer sendBeacon for unload-resilient delivery; fall back to fetch.
    const body = JSON.stringify(payload);
    let sent = false;
    if (typeof navigator !== "undefined" && "sendBeacon" in navigator) {
      try {
        const blob = new Blob([body], { type: "application/json" });
        sent = navigator.sendBeacon(ENDPOINT, blob);
      } catch {
        sent = false;
      }
    }
    if (!sent) {
      void fetch(ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body,
        // Browser sends cookies (_fbp, _fbc) automatically on same-origin POST.
        credentials: "same-origin",
        keepalive: true,
      }).catch(() => {
        // swallow
      });
    }
  }

  return eventId;
}

/**
 * Convenience: Purchase. The most valuable conversion to dedupe — also fired
 * server-side from Stripe/MercadoPago webhooks; the order_id ties browser
 * and server events together when event_id is unavailable.
 */
export function trackPurchaseDual(args: {
  value: number;
  currency: string;
  contentName?: string;
  contentIds?: string[];
  contentType?: string;
  orderId?: string;
  userData?: DualEventUserData;
}): string {
  return trackDualEvent(
    "Purchase",
    {
      value: args.value,
      currency: args.currency,
      ...(args.contentName ? { content_name: args.contentName } : {}),
      ...(args.contentIds ? { content_ids: args.contentIds } : {}),
      ...(args.contentType ? { content_type: args.contentType } : {}),
      ...(args.orderId ? { order_id: args.orderId } : {}),
    },
    args.userData,
  );
}

export function trackInitiateCheckoutDual(args: {
  value: number;
  currency: string;
  contentName?: string;
  userData?: DualEventUserData;
}): string {
  return trackDualEvent(
    "InitiateCheckout",
    {
      value: args.value,
      currency: args.currency,
      ...(args.contentName ? { content_name: args.contentName } : {}),
    },
    args.userData,
  );
}

/**
 * Convenience: Video engagement events (custom). Meta Audiences puede crear
 * Custom Audiences "vieron el video" / "miraron 75%+" para retargeting.
 *
 * Eventos custom (no estándar Pixel):
 *   - VideoViewStarted   → primer play del video (señal media de intent)
 *   - VideoViewCompleted → primer complete (señal alta de intent — ~3-5×
 *                          probabilidad de conversión vs solo started)
 *
 * Por qué custom y no AddToCart: AddToCart está reservado para acciones
 * EXPLÍCITAS del visitor (clickear "agregar carrito"). Video engagement es
 * passive — mejor usar custom events que el algoritmo de Meta interprete
 * según contexto. Si querés "warm audience" para retargeting, custom audience
 * "VideoViewCompleted en últimos 30 días" es el primary signal.
 *
 * content_ids = [variant_id] permite que Meta Audiences segmente automático
 * por variante A/B/C (e.g., "audience que vio variant_a en últimos 14 días").
 */
export function trackVideoViewStartedDual(args: {
  variantId?: string;
  playbackId?: string;
  country?: string;
  userData?: DualEventUserData;
}): string {
  return trackDualEvent(
    "VideoViewStarted",
    {
      content_type: "video",
      ...(args.variantId ? { content_ids: [args.variantId] } : {}),
      ...(args.playbackId ? { playback_id: args.playbackId } : {}),
      ...(args.country ? { country: args.country } : {}),
    },
    args.userData,
  );
}

export function trackVideoViewCompletedDual(args: {
  variantId?: string;
  playbackId?: string;
  watchedSeconds?: number;
  country?: string;
  userData?: DualEventUserData;
}): string {
  return trackDualEvent(
    "VideoViewCompleted",
    {
      content_type: "video",
      ...(args.variantId ? { content_ids: [args.variantId] } : {}),
      ...(args.playbackId ? { playback_id: args.playbackId } : {}),
      ...(args.watchedSeconds != null
        ? { watched_seconds: args.watchedSeconds }
        : {}),
      ...(args.country ? { country: args.country } : {}),
    },
    args.userData,
  );
}

/**
 * AddToCart — disparado cuando el visitor muestra intent CONCRETO de comprar
 * (e.g., click en botón VIP que ABRE el checkout). Es la señal más fuerte
 * pre-Purchase para el algoritmo de Meta. Sin AddToCart en el funnel, Meta
 * tiene que inferir el intent solo de InitiateCheckout → menos calidad de
 * optimization para "Conversions" campaigns.
 *
 * Distinto de InitiateCheckout en términos Meta:
 *   - AddToCart        = intent declarado (click "comprar")
 *   - InitiateCheckout = inició flow de pago (modal abierto)
 *
 * En nuestro flow ambos ocurren en el mismo click VIP pero los disparamos
 * separados con el mismo event_id → Meta los dedupe pero tiene los 2 signals.
 */
export function trackAddToCartDual(args: {
  value: number;
  currency: string;
  variantId?: string;
  contentName?: string;
  userData?: DualEventUserData;
}): string {
  return trackDualEvent(
    "AddToCart",
    {
      value: args.value,
      currency: args.currency,
      ...(args.contentName ? { content_name: args.contentName } : {}),
      ...(args.variantId ? { content_ids: [args.variantId] } : {}),
      content_type: "product",
    },
    args.userData,
  );
}

export function trackLeadDual(args: {
  contentName?: string;
  value?: number;
  currency?: string;
  userData?: DualEventUserData;
}): string {
  return trackDualEvent(
    "Lead",
    {
      ...(args.contentName ? { content_name: args.contentName } : {}),
      ...(args.value != null ? { value: args.value } : {}),
      ...(args.currency ? { currency: args.currency } : {}),
    },
    args.userData,
  );
}
